import React, { useState } from 'react'
import { Navigate, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Image,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Alert,
  AlertIcon,
  Link,
  HStack,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { motion } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import CoreTechLogo from '@/assets/logo-white.svg'
import { MotionBox } from '@/components/Common/MotionBox'

// Create motion components
const MotionVStack = motion(VStack as any)
const MotionHeading = motion(Heading as any)
const MotionText = motion(Text as any)
const MotionFormControl = motion(FormControl as any)
const MotionButton = motion(Button as any)
const MotionHStack = motion(HStack as any)

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
}

const titleVariants = {
  hidden: {
    opacity: 0,
    y: -20,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
}

const buttonVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.2,
    },
  },
  tap: {
    scale: 0.98,
  },
}

// Animation keyframes for geometric patterns
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
`

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.1); }
`

const drift = keyframes`
  0% { transform: translateX(-100px) translateY(0px); }
  50% { transform: translateX(100px) translateY(-50px); }
  100% { transform: translateX(-100px) translateY(0px); }
`

const AnimatedBackground = () => (
  <Box position="relative" w="full" h="full" overflow="hidden">
    {/* Gradient Background */}
    <Box
      position="absolute"
      top={0}
      left={0}
      w="full"
      h="full"
      // bgGradient="linear(135deg, blue.400 0%, blue.500 50%, blue.600 100%)"
      bgGradient="linear(135deg, blue.500 0%, blue.700 50%, blue.800 100%)"
    />

    {/* Animated Geometric Patterns */}
    <Box position="absolute" top="10%" left="10%" w="60px" h="60px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.200"
        borderRadius="lg"
        sx={{
          animation: `${float} 6s ease-in-out infinite`,
          animationDelay: '0s',
        }}
      />
    </Box>

    <Box position="absolute" top="30%" right="15%" w="80px" h="80px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.150"
        borderRadius="50%"
        sx={{
          animation: `${pulse} 4s ease-in-out infinite`,
          animationDelay: '1s',
        }}
      />
    </Box>

    <Box position="absolute" bottom="20%" left="20%" w="40px" h="40px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.300"
        transform="rotate(45deg)"
        sx={{
          animation: `${float} 8s ease-in-out infinite`,
          animationDelay: '2s',
        }}
      />
    </Box>

    <Box position="absolute" top="60%" left="60%" w="100px" h="100px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.100"
        borderRadius="lg"
        sx={{
          animation: `${drift} 12s linear infinite`,
          animationDelay: '0.5s',
        }}
      />
    </Box>

    <Box position="absolute" bottom="40%" right="25%" w="50px" h="50px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.250"
        borderRadius="md"
        sx={{
          clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
          animation: `${pulse} 5s ease-in-out infinite`,
          animationDelay: '3s',
        }}
      />
    </Box>

    {/* Particle System - Small floating dots */}
    {Array.from({ length: 15 }).map((_, i) => (
      <Box
        key={i}
        position="absolute"
        w="4px"
        h="4px"
        bg="whiteAlpha.300"
        borderRadius="50%"
        top={`${Math.random() * 100}%`}
        left={`${Math.random() * 100}%`}
        sx={{
          animation: `${float} ${4 + Math.random() * 4}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`,
        }}
      />
    ))}

    {/* Larger geometric elements */}
    <Box position="absolute" top="15%" left="70%" w="120px" h="120px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.100"
        borderRadius="50%"
        sx={{
          animation: `${pulse} 7s ease-in-out infinite`,
          animationDelay: '1.5s',
        }}
      />
    </Box>

    <Box position="absolute" bottom="30%" left="5%" w="90px" h="90px">
      <Box
        w="full"
        h="full"
        bg="whiteAlpha.150"
        borderRadius="md"
        transform="rotate(30deg)"
        sx={{
          animation: `${float} 9s ease-in-out infinite`,
          animationDelay: '2.5s',
        }}
      />
    </Box>
  </Box>
)

export default function LoginDesktopPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { login, isAuthenticated } = useAuth()

  // Only redirect if authenticated AND not in error/loading state
  if (isAuthenticated && !isLoading && !error) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login(email, password)
      // Navigation will happen automatically via AuthContext
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box position="relative" h="100vh" overflow="hidden">
      {/* Full Background */}
      <AnimatedBackground />

      {/* Login Form Overlay */}
      <MotionBox
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        w="full"
        maxW="450px"
        p={8}
        zIndex={10}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <MotionVStack spacing={8} variants={containerVariants}>
          <MotionVStack spacing={3} textAlign="center" variants={itemVariants}>
            <Image src={CoreTechLogo} alt="Coretech" h="100px" />
            <MotionHeading size="xl" color="white" fontWeight="bold" variants={titleVariants}>
              Welcome Back
            </MotionHeading>
            <MotionText color="whiteAlpha.900" fontSize="md" variants={itemVariants}>
              Access your document management system
            </MotionText>
          </MotionVStack>

          {error && (
            <Alert
              status="error"
              borderRadius="lg"
              bg="red.500"
              color="white"
              border="1px solid"
              borderColor="red.400"
            >
              <AlertIcon color="white" />
              {error}
            </Alert>
          )}

          <MotionBox as="form" onSubmit={handleSubmit} w="full" variants={itemVariants}>
            <MotionVStack spacing={6} variants={containerVariants}>
              <MotionFormControl isRequired variants={itemVariants}>
                <FormLabel color="white" fontWeight="medium" fontSize="sm">
                  Email Address
                </FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  size="lg"
                  borderRadius="lg"
                  bg="whiteAlpha.200"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _placeholder={{ color: 'whiteAlpha.700' }}
                  _hover={{
                    borderColor: 'whiteAlpha.500',
                    bg: 'whiteAlpha.250',
                  }}
                  _focus={{
                    borderColor: 'white',
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.8)',
                    bg: 'whiteAlpha.300',
                  }}
                />
              </MotionFormControl>

              <MotionFormControl isRequired variants={itemVariants}>
                <FormLabel color="white" fontWeight="medium" fontSize="sm">
                  Password
                </FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  size="lg"
                  borderRadius="lg"
                  bg="whiteAlpha.200"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  color="white"
                  _placeholder={{ color: 'whiteAlpha.700' }}
                  _hover={{
                    borderColor: 'whiteAlpha.500',
                    bg: 'whiteAlpha.250',
                  }}
                  _focus={{
                    borderColor: 'white',
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.8)',
                    bg: 'whiteAlpha.300',
                  }}
                />
              </MotionFormControl>

              <MotionButton
                type="submit"
                w="full"
                size="lg"
                bg="white"
                color="blue.600"
                isLoading={isLoading}
                loadingText="Signing in..."
                borderRadius="lg"
                fontWeight="semibold"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                _hover={{
                  boxShadow: 'xl',
                  bg: 'whiteAlpha.900',
                }}
                _active={{
                  bg: 'whiteAlpha.800',
                }}
              >
                Sign In
              </MotionButton>

              <MotionHStack justify="center" w="full" variants={itemVariants}>
                <Link
                  as={RouterLink}
                  to="/forgot-password"
                  fontSize="sm"
                  color="white"
                  _hover={{
                    color: 'whiteAlpha.800',
                    textDecoration: 'underline',
                  }}
                >
                  Forgot your password?
                </Link>
              </MotionHStack>
            </MotionVStack>
          </MotionBox>
        </MotionVStack>
      </MotionBox>
    </Box>
  )
}
